import moment from 'moment'
import { Expose, Transform, Type } from 'class-transformer'
import { AxiosError } from 'axios'
import { List } from 'react-feather'

import { skuit } from 'src/services/skuit'
import { ActionDirection, WorkflowSequence } from 'src/modules/supply_chain/components/Workflow'
import { handleErrorMessage } from 'src/components/Notification'

import { Address } from './Address'
import { Base } from './Base'
import { Carrier } from './Carrier'
import { Client } from './Client'
import { CustomFieldValue } from './CustomFieldValue'
import { Facility } from './Facility'
import { Flag } from './Flag'

enum ParcelOrderStatus {
  received = 'Received',
  processing = 'Processing',
  processed = 'Processed',
  shipped = 'Shipped',
  delivered = 'Delivered',
  canceled = 'Canceled',
}

export enum ParcelOrderStatusEnum {
  received = 'received',
  processing = 'processing',
  processed = 'processed',
  shipped = 'shipped',
  delivered = 'delivered',
  canceled = 'canceled',
}

export class ParcelOrder extends Base {
  static type = 'parcelorder'
  static typeDisplay = 'Parcel Order'
  static icon = List

  static Status = ParcelOrderStatus

  type = ParcelOrder.type
  typeDisplay = ParcelOrder.typeDisplay
  icon = ParcelOrder.icon

  @Expose({ name: 'xid' })
  guid: string = ''

  number?: string

  details?: string

  name: string = ''

  @Type(() => Client)
  client?: Client

  @Type(() => Carrier)
  carrier?: Carrier

  @Type(() => Facility)
  facility?: Facility

  consignee?: string

  @Expose({ name: 'custom_fields' })
  @Type(() => CustomFieldValue)
  customFields?: CustomFieldValue[]

  status?: string

  @Type(() => Address)
  address?: Address

  @Expose({ name: 'placed_on' })
  @Transform((value) => moment(value))
  placedOn?: moment.Moment

  @Expose({ name: 'processing_on' })
  @Transform((value) => (value ? moment(value) : value))
  processingOn?: moment.Moment

  @Expose({ name: 'processed_on' })
  @Transform((value) => (value ? moment(value) : value))
  processedOn?: moment.Moment

  @Expose({ name: 'shipped_on' })
  @Transform((value) => (value ? moment(value) : value))
  shippedOn?: moment.Moment

  @Expose({ name: 'delivered_on' })
  @Transform((value) => (value ? moment(value) : value))
  deliveredOn?: moment.Moment

  @Expose({ name: 'canceled_on' })
  @Transform((value) => (value ? moment(value) : value))
  canceledOn?: moment.Moment

  @Expose({ name: 'fulfill_on' })
  @Transform((value) => value ? moment(value) : value)
  fulfillOn?: moment.Moment

  facilities?: Facility[]

  @Type(() => Flag)
  flags?: Flag[]

  get workflowStatus() {
    if (this.status) {
      return this.status.charAt(0).toUpperCase() + this.status.slice(1)
    }
    return ''
  }

  get workflow(): WorkflowSequence {
    if (this.status === ParcelOrderStatusEnum.canceled) {
      return [
        [ParcelOrderStatus.received, this.placedOn],
        [ParcelOrderStatus.processing, this.processingOn],
        [ParcelOrderStatus.processed, this.processedOn],
        [ParcelOrderStatus.canceled, this.canceledOn],
      ]
    }
    return [
      [ParcelOrderStatus.received, this.placedOn],
      [ParcelOrderStatus.processing, this.processingOn],
      [ParcelOrderStatus.processed, this.processedOn],
      [ParcelOrderStatus.shipped, this.shippedOn],
      [ParcelOrderStatus.delivered, this.deliveredOn],
    ]
  }

  getWorkflowActions = (onAction?: () => void): { [status: string]: ActionDirection } => {
    return {
      [ParcelOrderStatus.received]: {
        forward: [
          {
            label: 'Start Processing',
            onClick: ({ complete }) => {
              skuit.sc.parcel.orders
                .patch(this.guid, { status: 'processing' })
                .then(() => onAction?.())
                .catch((error: AxiosError) => handleErrorMessage(error, 'updat', 'Parcel Order'))
                .finally(() => complete())
            },
          },
        ],
        cancel: [
          {
            label: 'Cancel',
            onClick: ({ complete }) => {
              skuit.sc.parcel.orders
                .patch(this.guid, { status: 'canceled' })
                .then(() => onAction?.())
                .catch((error: AxiosError) => handleErrorMessage(error, 'updat', 'Parcel Order'))
                .finally(() => complete())
            },
          },
        ],
      },
      [ParcelOrderStatus.processing]: {
        forward: [
          {
            label: 'Complete Processing',
            onClick: ({ complete, setConfirmation }) => {
              skuit.sc.parcel.orders
                .patch(this.guid, { status: 'processed' })
                .then(() => onAction?.())
                .catch((error: AxiosError) => {
                  if (
                    // eslint-disable-next-line camelcase
                    error?.response?.data.non_field_errors?.includes(
                      'Order is not completely fulfilled'
                    )
                  ) {
                    setConfirmation?.({
                      error: error.response.data.non_field_errors,
                      prompt: 'Do you want to override? Careful, this could lead to short shipping this order?.',
                      submit: 'Mark Processed',
                      action: ({ complete }) => {
                        skuit.sc.parcel.orders
                          .patch(this.guid, { status: 'processed', short_ship: true })
                          .then(() => onAction?.())
                          .catch((error: AxiosError) => {
                            handleErrorMessage(error, 'updat', 'Parcel Order')
                          })
                          .finally(() => complete())
                      },
                    })
                  } else {
                    handleErrorMessage(error, 'updat', 'Parcel Order')
                  }
                })
                .finally(() => complete())
            },
          },
        ],
        cancel: [
          {
            label: 'Cancel',
            onClick: ({ complete }) => {
              skuit.sc.parcel.orders
                .patch(this.guid, { status: 'canceled' })
                .then(() => onAction?.())
                .catch((error: AxiosError) => handleErrorMessage(error, 'updat', 'Parcel Order'))
                .finally(() => complete())
            },
          },
        ],
      },
      [ParcelOrderStatus.processed]: {
        forward: [
          {
            label: 'Ship',
            onClick: ({ complete, setConfirmation }) => {
              skuit.sc.parcel.orders
                .patch(this.guid, { status: 'shipped' })
                .then(() => onAction?.())
                .catch((error: AxiosError) => {
                  if (
                    // eslint-disable-next-line camelcase
                    error?.response?.data.non_field_errors?.includes(
                      'Order is not completely fulfilled'
                    )
                  ) {
                    setConfirmation?.({
                      error: error.response.data.non_field_errors,
                      prompt: 'Do you want to override and short ship this order?.',
                      submit: 'Short Ship',
                      action: ({ complete }) => {
                        skuit.sc.parcel.orders
                          .patch(this.guid, { status: 'shipped', short_ship: true })
                          .then(() => onAction?.())
                          .catch((error: AxiosError) => {
                            handleErrorMessage(error, 'updat', 'Parcel Order')
                          })
                          .finally(() => complete())
                      },
                    })
                  } else {
                    handleErrorMessage(error, 'updat', 'Parcel Order')
                  }
                })
                .finally(() => complete())
            },
          },
        ],
        backward: [
          {
            label: 'Resume Processing',
            onClick: ({ complete }) => {
              skuit.sc.parcel.orders
                .patch(this.guid, { status: 'processing' })
                .then(() => onAction?.())
                .catch((error: AxiosError) => handleErrorMessage(error, 'updat', 'Parcel Order'))
                .finally(() => complete())
            },
          },
        ],
        cancel: [
          {
            label: 'Cancel',
            onClick: ({ complete }) => {
              skuit.sc.parcel.orders
                .patch(this.guid, { status: 'canceled' })
                .then(() => onAction?.())
                .catch((error: AxiosError) => handleErrorMessage(error, 'updat', 'Parcel Order'))
                .finally(() => complete())
            },
          },
        ],
      },
      [ParcelOrderStatus.shipped]: {
        forward: [
          {
            label: 'Deliver',
            onClick: ({ complete }) => {
              skuit.sc.parcel.orders
                .patch(this.guid, { status: 'delivered' })
                .then(() => onAction?.())
                .catch((error: AxiosError) => handleErrorMessage(error, 'updat', 'Parcel Order'))
                .finally(() => complete())
            },
          },
        ],
        backward: [
          {
            label: 'Unship',
            onClick: ({ complete }) => {
              skuit.sc.parcel.orders
                .patch(this.guid, { status: 'processed' })
                .then(() => onAction?.())
                .catch((error: AxiosError) => handleErrorMessage(error, 'updat', 'Parcel Order'))
                .finally(() => complete())
            },
          },
        ],
      },
      [ParcelOrderStatus.delivered]: {
        backward: [
          {
            label: 'Undeliver',
            onClick: ({ complete }) => {
              skuit.sc.parcel.orders
                .patch(this.guid, { status: 'shipped' })
                .then(() => onAction?.())
                .catch((error: AxiosError) => handleErrorMessage(error, 'updat', 'Parcel Order'))
                .finally(() => complete())
            },
          },
        ],
      },
      [ParcelOrderStatus.canceled]: {
        backward: [
          {
            label: 'Uncancel',
            onClick: ({ complete }) => {
              skuit.sc.parcel.orders
                .patch(this.guid, { status: 'received' })
                .then(() => onAction?.())
                .catch((error: AxiosError) => handleErrorMessage(error, 'updat', 'Parcel Order'))
                .finally(() => complete())
            },
          },
        ],
      },
    }
  }
}

export const parcelOrderStatusToString = (status: ParcelOrderStatusEnum): string => {
  return status[0].toUpperCase() + status.slice(1)
}

export const nextParcelOrderStatus = (actualStatus: string) => {
  switch (actualStatus) {
    case 'received':
      return ParcelOrderStatusEnum.processing
    case 'processing':
      return ParcelOrderStatusEnum.processed
    case 'processed':
      return ParcelOrderStatusEnum.shipped
    case 'shipped':
      return ParcelOrderStatusEnum.delivered
    default:
      return actualStatus
  }
}
